import React, { useCallback, useEffect, useState } from 'react';
import api from '../../lib/api';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { CalendarPlus, Video, MapPin, Phone } from 'lucide-react';
import { MeetingFormDialog } from './MeetingFormDialog';
import { OutcomeDialog } from './OutcomeDialog';
import { MEETING_STATUS_DOT, OUTCOME_COLORS, fmtMeetingDate, fmtMeetingRange } from '../../lib/meetings';

const MODE_ICON = { 'In-Person': MapPin, 'Video Call': Video, 'Phone Call': Phone, 'WhatsApp Call': Phone };

const MeetingEntry = ({ m, isLast, onOutcome }) => {
  const ModeIcon = MODE_ICON[m.mode] || Video;
  return (
    <div className="relative pl-6 pb-4" data-testid={`lead-meeting-${m.id}`}>
      {!isLast && <span className="absolute left-[5px] top-4 bottom-0 w-px bg-border" aria-hidden="true" />}
      <span className={`absolute left-0 top-1.5 h-[11px] w-[11px] rounded-full ring-4 ring-background ${MEETING_STATUS_DOT[m.status] || 'bg-slate-400'}`} aria-hidden="true" />
      <div className="flex items-center justify-between gap-2">
        <span className="text-xs font-semibold truncate">{m.title}</span>
        <span className="text-[10px] text-muted-foreground whitespace-nowrap">{fmtMeetingDate(m.start_at)}</span>
      </div>
      <div className="text-[11px] text-muted-foreground flex items-center gap-1.5 flex-wrap mt-0.5">
        <span className="inline-flex items-center gap-1"><ModeIcon size={11} /> {m.mode}</span>
        <span>· {fmtMeetingRange(m)}</span>
        <span>· {m.meeting_type}</span>
      </div>
      <div className="flex items-center gap-1.5 mt-1 flex-wrap">
        <Badge variant="outline" className="text-[10px]">{m.status}</Badge>
        {m.outcome && (
          <Badge variant="outline" className={`text-[10px] ${OUTCOME_COLORS[m.outcome.outcome] || ''}`}>{m.outcome.outcome}</Badge>
        )}
        {m.outcome_pending && (
          <button type="button" onClick={() => onOutcome(m)}
            className="text-[10px] font-medium text-red-600 border border-red-300 bg-red-50 rounded-full px-2 py-0.5 hover:bg-red-100"
            data-testid={`lead-meeting-record-outcome-${m.id}`}>
            Outcome Pending — record now
          </button>
        )}
      </div>
      {m.outcome?.next_action && (
        <div className="text-[11px] text-muted-foreground mt-0.5">Next: {m.outcome.next_action}{m.outcome.next_action_date ? ` by ${m.outcome.next_action_date}` : ''}</div>
      )}
    </div>
  );
};

/** Meetings timeline for the Lead drawer — newest first, with 1-tap scheduling. */
export const LeadMeetingsTab = ({ lead, canCreate = true }) => {
  const [meetings, setMeetings] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [outcomeFor, setOutcomeFor] = useState(null);

  const load = useCallback(() => {
    if (!lead?.id) return;
    api.get(`/meetings/lead/${lead.id}`).then(({ data }) => { setMeetings(data); setLoaded(true); })
      .catch(() => setLoaded(true));
  }, [lead?.id]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-3" data-testid="lead-meetings-tab">
      <div className="flex items-center justify-between">
        <div className="text-xs text-muted-foreground">
          {meetings.length} meeting{meetings.length === 1 ? '' : 's'} — synced with the Meetings module
        </div>
        {canCreate && (
          <Button size="sm" variant="secondary" className="h-8" onClick={() => setFormOpen(true)} data-testid="lead-schedule-meeting-btn">
            <CalendarPlus size={13} className="mr-1.5" /> Schedule Meeting
          </Button>
        )}
      </div>
      <div>
        {meetings.map((m, i) => (
          <MeetingEntry key={m.id} m={m} isLast={i === meetings.length - 1} onOutcome={setOutcomeFor} />
        ))}
        {loaded && meetings.length === 0 && (
          <div className="text-xs text-muted-foreground text-center py-6">No meetings yet — schedule the first one</div>
        )}
      </div>
      <MeetingFormDialog open={formOpen} onClose={() => setFormOpen(false)} lead={lead} onSaved={load} />
      <OutcomeDialog open={!!outcomeFor} onClose={() => setOutcomeFor(null)} meeting={outcomeFor} onSaved={load} />
    </div>
  );
};
